// Activity (brief section 8.7): assistant runs with model, cost and outcome; the audit history.
import { Link } from 'react-router';
import { useContextWorkspace } from '../components/Layout';
import { Chip, Empty, ErrorBox, Loading, LockIcon, PageHead } from '../components/ui';
import { seconds, usd, when, words } from '../lib/format';
import type { ActivityRun, AuditEvent } from '../lib/types';
import { useApi } from '../lib/useApi';

interface ActivityData {
  events: AuditEvent[];
  runs: ActivityRun[];
  spend: { committed_this_month_usd: number; monthly_budget_usd: number; run_budget_usd?: number };
}

export function Activity() {
  const ws = useContextWorkspace();
  const a = useApi<ActivityData>(ws ? `/api/w/${ws.slug}/activity` : '/api/activity');
  const base = ws ? `/w/${ws.slug}/assistant` : '/assistant';
  return (
    <>
      <PageHead
        title={ws ? 'Activity' : 'My activity'}
        sub={ws ? `What happened in ${ws.name}. Private actions are listed to their author only.` : 'Your sign-ins and your personal assistant.'}
      />
      {a.error && <ErrorBox error={a.error} retry={a.reload} />}
      {!a.data && !a.error && <Loading />}
      {a.data && (
        <>
          <section className="panel">
            <h2>Spend this month</h2>
            <p>
              <strong>{usd(a.data.spend.committed_this_month_usd)}</strong> of {usd(a.data.spend.monthly_budget_usd)}
              {a.data.spend.run_budget_usd !== undefined && <span className="muted"> · each answer stops at {usd(a.data.spend.run_budget_usd)}</span>}
            </p>
            <p className="muted small">Measured cost reported by Claude Code, plus the reserved ceiling of answers still in progress.</p>
          </section>
          <section className="panel">
            <h2>Assistant runs</h2>
            {a.data.runs.length === 0 ? (
              <Empty>No assistant run yet.</Empty>
            ) : (
              <div className="table-wrap stack-on-phone">
                <table>
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Conversation</th>
                      <th>Asked by</th>
                      <th>Model</th>
                      <th>Outcome</th>
                      <th className="num">Cost</th>
                      <th className="num">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.data.runs.map((r) => (
                      <tr key={r.id}>
                        <td data-label="When">{when(r.created_at)}</td>
                        <td data-label="Conversation">
                          <Link to={`${base}/${r.conversation_id}`}>
                            {r.private && <LockIcon />} {r.conversation}
                          </Link>
                        </td>
                        <td data-label="Asked by">{r.asked_by}</td>
                        <td data-label="Model">
                          <span>
                            {r.model ?? r.model_requested}
                            <span className="muted small"> · {r.effort}</span>
                          </span>
                        </td>
                        <td data-label="Outcome">
                          <div>
                            <Chip value={r.status} />
                            {r.error && <div className="muted small">{r.error}</div>}
                          </div>
                        </td>
                        <td className="num" data-label="Cost">{usd(r.cost_usd, 4)}</td>
                        <td className="num" data-label="Time">{seconds(r.duration_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
          <section className="panel">
            <h2>History</h2>
            {a.data.events.length === 0 ? (
              <Empty>Nothing recorded yet.</Empty>
            ) : (
              <ul className="events">
                {a.data.events.map((e) => (
                  <li key={e.id}>
                    <span className="muted small">{when(e.at)}</span> <strong>{e.who}</strong> {words(e.action.replace('.', ' · '))}
                    {e.private && (
                      <span className="badge private">
                        <LockIcon /> private
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </>
  );
}
